import { useEffect, useMemo, useRef, useState } from 'react';
import { countryByDialPrefix, flagEmoji, type Country } from '@jfh/intake-shared';

/** Searchable country-code dropdown + number, mirroring the public form picker. */
export function PhoneField({
  value,
  onChange,
  countries,
  defaultIso,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  countries: Country[];
  defaultIso: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  const list = countries.length ? countries : [{ iso2: 'GB', name: 'United Kingdom', dial: '+44' }];
  const matched = value ? countryByDialPrefix(value) : undefined;
  const fallback = list.find((c) => c.iso2 === defaultIso) ?? list[0]!;
  const [iso, setIso] = useState(matched?.iso2 ?? defaultIso);
  const country = list.find((c) => c.iso2 === iso) ?? fallback;
  const dial = (matched ?? country).dial;
  const number = matched ? value.slice(matched.dial.length).trim() : value.replace(/^\+\d+\s*/, '');
  const emit = (d: string, n: string) => onChange(n.trim() ? `${d} ${n.trim()}` : '');

  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <CodeSelect
        list={list}
        value={country.iso2}
        dial={dial}
        disabled={disabled}
        onChange={(v) => {
          setIso(v);
          emit(list.find((c) => c.iso2 === v)?.dial ?? dial, number);
        }}
      />
      <input
        className="input"
        type="tel"
        style={{ flex: 1, minWidth: 0 }}
        disabled={disabled}
        placeholder={placeholder ?? 'Phone number'}
        value={number}
        onChange={(e) => emit(dial, e.target.value)}
      />
    </div>
  );
}

function CodeSelect({
  list,
  value,
  dial,
  disabled,
  onChange,
}: {
  list: Country[];
  value: string;
  dial: string;
  disabled?: boolean;
  onChange: (iso: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  useEffect(() => {
    if (open) {
      setQ('');
      inputRef.current?.focus();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? list.filter((c) => `${c.name} ${c.dial} ${c.iso2}`.toLowerCase().includes(n)) : list;
  }, [list, q]);

  return (
    <div ref={ref} style={{ position: 'relative', flex: 'none' }}>
      <button
        type="button"
        className="input"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: 'pointer' }}
      >
        {flagEmoji(value)} {dial} <span style={{ color: 'var(--text-3)' }}>▾</span>
      </button>
      {open && (
        <div
          className="card"
          style={{
            position: 'absolute',
            zIndex: 50,
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 260,
            maxHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'var(--sh-md)',
            overflow: 'hidden',
          }}
        >
          <input
            ref={inputRef}
            className="input"
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ margin: 8, width: 'auto' }}
          />
          <div style={{ overflowY: 'auto' }}>
            {filtered.map((c) => (
              <button
                key={c.iso2}
                type="button"
                onClick={() => {
                  onChange(c.iso2);
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '7px 12px',
                  border: 'none',
                  background: c.iso2 === value ? 'var(--accent-soft)' : 'transparent',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                {flagEmoji(c.iso2)} {c.name} ({c.dial})
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
